import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import './ScrollBar.css';

export type ScrollBarType = 'vertical' | 'horizontal';

export type ScrollBarProps = {
  /** Ref to the scrollable container this bar tracks. When omitted the bar renders as a static decorative indicator. */
  containerRef?: RefObject<HTMLElement | null>;
  type?: ScrollBarType;
  className?: string;
};

/** Minimum thumb size, but never larger than the track (compact single-line fields). */
const MIN_THUMB_PX = 8;

export function overlayThumbHeight(
  clientHeight: number,
  scrollHeight: number,
  trackH: number,
): number {
  if (trackH <= 0 || scrollHeight <= clientHeight) {
    return 0;
  }
  const proportional = (clientHeight / scrollHeight) * trackH;
  return Math.min(trackH, Math.max(Math.min(MIN_THUMB_PX, trackH), proportional));
}

/**
 * Scrollbar indicator.
 *
 * When `containerRef` is provided the bar becomes **functional**: it reflects
 * the container's scroll position, auto-hides when content fits, and supports
 * drag-to-scroll on the thumb.
 *
 * Without `containerRef` it renders a static decorative indicator (backward
 * compatible with existing Storybook stories).
 */
export function ScrollBar({ containerRef, type = 'vertical', className = '' }: ScrollBarProps) {
  /* ── Decorative (static) fallback ────────────────────────────────── */
  if (!containerRef) {
    const cls = [
      'spark-scrollbar',
      type === 'horizontal' ? 'spark-scrollbar-horizontal' : 'spark-scrollbar-vertical',
      className,
    ].filter(Boolean).join(' ');

    return (
      <div className={cls} role="presentation" aria-hidden="true">
        <div className="spark-scrollbar-thumb" />
      </div>
    );
  }

  /* ── Functional mode ─────────────────────────────────────────────── */
  return <FunctionalScrollBar containerRef={containerRef} className={className} />;
}

/* Extracted into its own component so hooks are not called conditionally. */
function FunctionalScrollBar({
  containerRef,
  className,
}: {
  containerRef: RefObject<HTMLElement | null>;
  className: string;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [thumbH, setThumbH] = useState(0);
  const [thumbY, setThumbY] = useState(0);
  const [visible, setVisible] = useState(false);
  const dragRef = useRef<{ startY: number; startScroll: number } | null>(null);

  const update = useCallback(() => {
    const el = containerRef.current;
    const track = trackRef.current;
    if (!el || !track) { setVisible(false); return; }

    const { scrollTop, scrollHeight, clientHeight } = el;

    // Supported layout is a sibling overlay of the scroller (Modal, ModelSelection).
    // If the bar is still a child of the overflow box, pin with sticky — never
    // `top`/`translateY`(scrollTop), which inflates scrollHeight and leaves a
    // blank region the user can keep scrolling into.
    const root = rootRef.current;
    if (root && el.contains(root)) {
      root.style.position = 'sticky';
      root.style.top = '0';
      root.style.bottom = 'auto';
      root.style.height = `${clientHeight}px`;
      root.style.transform = '';
    }
    const trackH = track.clientHeight;
    if (scrollHeight <= clientHeight + 1) { setVisible(false); return; }

    setVisible(true);
    const th = overlayThumbHeight(clientHeight, scrollHeight, trackH);
    const maxY = Math.max(0, trackH - th);
    const ratio = scrollTop / (scrollHeight - clientHeight);
    setThumbH(th);
    setThumbY(ratio * maxY);
  }, [containerRef]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    el.addEventListener('scroll', update, { passive: true });
    el.addEventListener('input', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    update();

    return () => {
      el.removeEventListener('scroll', update);
      el.removeEventListener('input', update);
      ro.disconnect();
    };
  }, [containerRef, update]);

  const onThumbDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const el = containerRef.current;
    if (!el) return;
    dragRef.current = { startY: e.clientY, startScroll: el.scrollTop };

    const onMove = (ev: MouseEvent) => {
      const d = dragRef.current;
      const el = containerRef.current;
      const track = trackRef.current;
      if (!d || !el || !track) return;
      const { scrollHeight, clientHeight } = el;
      const trackH = track.clientHeight;
      const th = overlayThumbHeight(clientHeight, scrollHeight, trackH);
      const maxY = trackH - th;
      if (maxY <= 0) return;
      const maxScroll = scrollHeight - clientHeight;
      const dy = ev.clientY - d.startY;
      el.scrollTop = d.startScroll + (dy / maxY) * maxScroll;
    };
    const onUp = () => {
      dragRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [containerRef]);

  const cls = [
    'spark-scrollbar',
    'spark-scrollbar-functional',
    !visible && 'spark-scrollbar-hidden',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={cls} role="scrollbar" aria-hidden="true" ref={rootRef}>
      <div className="spark-scrollbar-track" ref={trackRef}>
        <div
          className="spark-scrollbar-thumb"
          style={{ height: thumbH, top: thumbY }}
          onMouseDown={onThumbDown}
        />
      </div>
    </div>
  );
}
